export type DeliveryStatus = "pending" | "sent" | "delivered" | "read" | "failed";

export type Presence = "online" | "idle" | "offline" | "warming";

export type ChatMessage = {
  /** Unique UI / IndexedDB key (user turn id, or `assistant:<turnId>`). */
  id: string;
  /** MQTT envelope id correlating user turn ↔ assistant reply. */
  turnId?: string;
  ariaId: string;
  role: "user" | "assistant" | "system";
  text: string;
  ts: string;
  delivery?: DeliveryStatus;
  streaming?: boolean;
};

export type AriaContact = {
  ariaId: string;
  name: string;
  hostname?: string;
  labels: Record<string, string>;
  presence: Presence;
  lastSeenAt?: string;
  warm?: boolean;
  lastMessage?: { text: string; ts: string; role: "user" | "assistant" };
  unreadCount: number;
  typing?: boolean;
};

export type FleetEnvelope = {
  v: 1;
  type: string;
  id: string;
  ts: string;
  agentId: string;
  payload: Record<string, unknown>;
};

export type MqttCredentials = {
  wsUrl: string;
  username: string;
  password: string;
};

export type DeviceSession = {
  deviceId: string;
  deviceToken: string;
  operatorName: string;
  pairedAt: string;
  expiresAt: string;
};

export type PairingState =
  | { phase: "idle" }
  | { phase: "connecting" }
  | { phase: "awaiting_approval"; code: string; expiresAt: string }
  | { phase: "paired" }
  | { phase: "error"; message: string };

export type ConnectionPhase =
  | "disconnected"
  | "connecting"
  | "connected"
  | "reconnecting";

export const TOPICS = {
  ariaAnnounce: "mxpf/v1/aria/registry/announce",
  ariaStatus: (ariaId: string) => `mxpf/v1/aria/${ariaId}/status`,
  ariaStatusAll: "mxpf/v1/aria/+/status",
  webIn: (ariaId: string) => `mxpf/v1/aria/${ariaId}/web/in`,
  webOutAll: "mxpf/v1/aria/+/web/out/#",
} as const;

export function makeEnvelope(
  type: string,
  agentId: string,
  payload: Record<string, unknown>,
  id: string = crypto.randomUUID(),
): FleetEnvelope {
  return {
    v: 1,
    type,
    id,
    ts: new Date().toISOString(),
    agentId,
    payload,
  };
}

export function parseEnvelope(raw: string | Buffer): FleetEnvelope {
  const text = typeof raw === "string" ? raw : raw.toString("utf8");
  return JSON.parse(text) as FleetEnvelope;
}

export function presenceFromLastSeen(lastSeenAt?: string): Presence {
  if (!lastSeenAt) return "offline";
  const ms = Date.now() - Date.parse(lastSeenAt);
  if (!Number.isFinite(ms) || ms < 0) return "offline";
  if (ms <= 90_000) return "online";
  if (ms <= 30 * 60_000) return "idle";
  return "offline";
}

export function formatLastSeen(lastSeenAt?: string): string {
  if (!lastSeenAt) return "offline";
  const presence = presenceFromLastSeen(lastSeenAt);
  if (presence === "online") return "online";
  const ms = Date.now() - Date.parse(lastSeenAt);
  if (!Number.isFinite(ms) || ms < 0) return "offline";
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `last seen ${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `last seen ${hr}h ago`;
  return `last seen ${Math.floor(hr / 24)}d ago`;
}

export function formatMessageTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();
  if (sameDay) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

export function contactInitials(name: string): string {
  const parts = name.split(/[\s·.-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

/** Stable unique id for the assistant side of an MQTT turn. */
export function assistantMessageId(turnId: string): string {
  return `assistant:${turnId}`;
}

export function generatePairingCode(): string {
  return String(Math.floor(100_000 + Math.random() * 900_000));
}

export const DEVICE_TOKEN_TTL_MS = 30 * 24 * 60 * 60 * 1000;
export const PAIRING_CODE_TTL_MS = 5 * 60 * 1000;
